import React from 'react';
import {
  VStack,
  HStack,
  Box,
  ScrollView,
  Text,
  Switch,
} from '@gluestack-ui/themed';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormField from '@/common/components/forms/FormField';
import SettingsGroupCard from '@/common/components/forms/SettingsGroupCard';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import { useDialog } from '@/common/context/DialogContext';
import type { ModuleLecturerResponse } from '@/common/api/models/moduleLecturerResponse';
import { getWorkingModeFieldLock } from '@/lecturer/ModuleEditor/workingMode/workingModeFieldLocks';
import FormalCriteriaForm from './FormalCriteriaForm';
import DotLevelSelector from '../../ModuleMetaEditor/ModuleMetaForm/components/DotLevelSelector';

interface TaskEvalFormProps {
  module: ModuleLecturerResponse;
  isReadOnly?: boolean;
}

const TaskEvalForm: React.FC<TaskEvalFormProps> = ({ module, isReadOnly = true }) => {
  const { t } = useTranslation();
  const { watch, setValue, formState } = useFormContext();
  const [hasRubric, setHasRubric] = React.useState(false);
  const { evalDimensionKeys } = useTenantGradingSettings();
  const { showDialog } = useDialog();

  const { isFeatureActive } = useUserFeatures();
  const taskAiEnabled = isFeatureActive('lecturer-task-ai');
  const taskType = watch('type');
  const eventKindId = watch('eventConfig.eventKindId');
  const workingMode = module?.metadata?.workingMode;

  const completionPolicyLock = getWorkingModeFieldLock({
    workingMode,
    field: 'task.completionPolicyOverride',
    taskType,
    eventKindId,
  });
  const evaluationModeLock = getWorkingModeFieldLock({
    workingMode,
    field: 'task.evaluationMode',
    taskType,
    eventKindId,
  });
  const gradingRelevantLock = getWorkingModeFieldLock({
    workingMode,
    field: 'task.gradingRelevant',
    taskType,
    eventKindId,
  });

  const formatFieldLockedHint = (modeLabelKey?: string) => (
    modeLabelKey
      ? t('lecturer.moduleEditor.workingMode.fieldLocked', {
        mode: t(modeLabelKey),
      })
      : undefined
  );

  const rubric = watch('rubric');
  React.useEffect(() => {
    const numRubricItems = rubric && rubric?.dimensions && Array.isArray(rubric?.dimensions) ? rubric.dimensions.length : 0;

    setHasRubric(numRubricItems > 0);
    if (numRubricItems === 0) {
      setValue('evaluation.mode', 'manual');
    }
  }, [rubric, setValue]);

  // Determine whether gating is active at module level or by override
  const modulePolicy = module?.metadata?.taskCompletionPolicy || null;
  const overridePolicy = watch('evaluation.completionPolicyOverride');
  const isGatedActive = (modulePolicy === 'gated' && overridePolicy === 'inherit') || overridePolicy === 'gated';

  // If override switch is turned off, clear overrides
  const overrideMinLevels = watch('evaluation.overrideMinLevels');
  React.useEffect(() => {
    if (!overrideMinLevels) {
      setValue('evaluation.completionMinLevelsOverride', {});
    }
  }, [overrideMinLevels, setValue]);

  // Watch for AI Tutor toggle and validate rubric exists.
  const aiTutorEnabled = watch('evaluation.mode') === 'auto';
  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isUserChange = !!(formState?.dirtyFields?.evaluation && (formState.dirtyFields.evaluation as any).mode);
    if (aiTutorEnabled && !hasRubric && isUserChange) {
      setValue('evaluation.mode', 'manual');
      showDialog({
        title: 'Cannot Enable AI Tutor',
        message: 'Task does not have a rubric yet, you cannot turn on tutor.',
        buttons: [{ text: 'OK', action: 'primary' }],
      });
    }
  }, [aiTutorEnabled, hasRubric, formState.dirtyFields.evaluation, setValue, showDialog]);

  return (
    <Box flex={1} bg="$bgApp">
      <ScrollView flex={1} bg="$bgApp">
        <VStack space="xl" px="$6" py="$4">
          <HStack space="md" alignItems="flex-start" flexWrap="wrap">
            {taskType === 'essay' && (
              <SettingsGroupCard title="Essay Evaluation Settings" flex={1} minWidth={400}>
                <VStack space="lg">
                  <FormField
                    name="evaluation.gradingRelevant"
                    type="switch"
                    horizontal
                    readonly={isReadOnly || gradingRelevantLock.locked}
                    label='Grading Relevant ("Transfer Task")'
                    defaultValue={false}
                    fieldWidth={160}
                    labelSize="xs"
                    fieldSize="xs"
                    errorTextSize="2xs"
                    helperText={
                      gradingRelevantLock.locked
                        ? formatFieldLockedHint(gradingRelevantLock.modeLabelKey)
                        : undefined
                    }
                  />

                  {/* Formal criteria */}
                  <FormField
                    name="evaluation.formalCriteriaSource"
                    type="select"
                    horizontal
                    readonly={isReadOnly}
                    label="Formal Criteria"
                    choices={[
                      { label: 'Default', value: 'default' },
                      { label: 'Task Specific', value: 'task_params' },
                      ...(taskAiEnabled ? [{ label: 'Custom in Rubric', value: 'rubric' }] : []),
                    ]}
                    defaultValue="default"
                    labelSize="xs"
                    fieldSize="xs"
                    errorTextSize="2xs"
                  />
                  {watch('evaluation.formalCriteriaSource') === 'task_params' && (
                    <FormalCriteriaForm isReadOnly={isReadOnly} />
                  )}

                  <FormField
                    name="evaluation.attachmentPolicy"
                    type="select"
                    horizontal
                    readonly={isReadOnly}
                    label="Allowed Attachments"
                    choices={[
                      { label: 'No attachments', value: 'none' },
                      { label: 'Images only (PNG, JPG, JPEG)', value: 'images' },
                      { label: 'Extended (PNG, JPG, JPEG, PDF, IPYNB)', value: 'extended' },
                    ]}
                    fieldWidth={300}
                    defaultValue="none"
                    labelSize="xs"
                    fieldSize="xs"
                    errorTextSize="2xs"
                  />
                </VStack>
              </SettingsGroupCard>
            )}

            {/* Completion policy override */}
            <SettingsGroupCard title="Task Completion Settings" flex={1} minWidth={400}>
              <VStack space="lg">
                <FormField
                  name="evaluation.completionPolicyOverride"
                  type="select"
                  horizontal
                  readonly={isReadOnly || completionPolicyLock.locked}
                  label="Completion Policy Override"
                  choices={[
                    { label: 'Inherit from Module', value: 'inherit' },
                    { label: 'Free (no gating)', value: 'free' },
                    { label: 'Gated (require min levels)', value: 'gated' },
                  ]}
                  defaultValue="inherit"
                  labelSize="xs"
                  fieldSize="xs"
                  errorTextSize="2xs"
                  helperText={
                    completionPolicyLock.locked
                      ? formatFieldLockedHint(completionPolicyLock.modeLabelKey)
                      : undefined
                  }
                />

                {/* Per-dimension minimum level overrides */}
                {isGatedActive && (
                  <>
                    <FormField
                      name="evaluation.overrideMinLevels"
                      type="switch"
                      horizontal
                      readonly={isReadOnly}
                      label="Override Module Minimal Levels"
                      defaultValue={false}
                      fieldWidth={260}
                      labelSize="xs"
                      fieldSize="xs"
                      errorTextSize="2xs"
                    />

                    {!!watch('evaluation.overrideMinLevels') && (
                      <VStack space="xs" ml="$4" mt="$2">
                        {(evalDimensionKeys || []).map((dimKey: string) => (
                          <DotLevelSelector
                            key={`task-minLevel-${dimKey}`}
                            name={`evaluation.completionMinLevelsOverride.${dimKey}`}
                            dimension={dimKey}
                            readonly={isReadOnly}
                          />
                        ))}
                      </VStack>
                    )}
                  </>
                )}
              </VStack>
            </SettingsGroupCard>
          </HStack>

          {taskAiEnabled && taskType === 'essay' && (
            <SettingsGroupCard title="AI Tutor Settings" maxWidth={900}>
              <VStack space="xs">
                <Controller
                  name="evaluation.mode"
                  defaultValue="manual"
                  render={({ field: { value, onChange } }) => (
                    <HStack justifyContent="space-between" alignItems="center" w="$full" maxWidth={900}>
                      <Text size="xs" color="$textBody" flex={1} pr="$4">
                        Enable AI Tutor (requires rubric)
                      </Text>
                      <Switch
                        size="sm"
                        isDisabled={isReadOnly || !hasRubric || evaluationModeLock.locked}
                        value={value === 'auto'}
                        onToggle={(v: boolean) => onChange(v ? 'auto' : 'manual')}
                      />
                    </HStack>
                  )}
                />
                {evaluationModeLock.locked && (
                  <Text size="2xs" color="$textHint">
                    {formatFieldLockedHint(evaluationModeLock.modeLabelKey)}
                  </Text>
                )}
              </VStack>
            </SettingsGroupCard>
          )}
        </VStack>
      </ScrollView>
    </Box>
  );
};

export default TaskEvalForm;
